"use client";

import { useId, useRef, useState, type FormEvent } from "react";
import { submitForm } from "../../../../client";
import { RavioliRichText } from "../RavioliRichText/RavioliRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-12 w-full border border-line bg-canvas px-4 font-ravioli-body text-base text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-ink-muted focus:border-primary focus:shadow-ravioli-action-sm aria-[invalid=true]:border-selection aria-[invalid=true]:shadow-ravioli-selection-sm";

const getAutocomplete = (
  fieldType: IFormPanelProps["fields"][number]["fieldType"],
  label: string,
) => {
  if (fieldType === "email") return "email";
  if (fieldType === "phone") return "tel";
  if (fieldType === "short-text" && /name/iu.test(label)) return "name";
  return undefined;
};

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const failureRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const clearFieldError = (name: string) =>
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};
    for (const control of controls) {
      if (control instanceof HTMLInputElement && control.type === "tel") {
        const validPhone = !control.value || /^[+()0-9 .-]{7,}$/u.test(control.value);
        control.setCustomValidity(validPhone ? "" : "Enter a valid phone number.");
      }
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }

    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("offline");
      await submitForm({
        fields: fields.map((field, index) => {
          const control = controls.find((item) => item.name === `field-${index}`);
          return {
            label: field.label,
            type: field.fieldType,
            value:
              control instanceof HTMLInputElement && control.type === "checkbox"
                ? control.checked
                : control?.value || "",
          };
        }),
        sourcePath: window.location.pathname,
      });
      setSucceeded(true);
    } catch {
      setSubmissionFailed(true);
      window.requestAnimationFrame(() => failureRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="border border-primary bg-collection p-7 text-ink shadow-ravioli-action-md md:p-10"
        role="status"
      >
        <RavioliRichText
          className="max-w-[65ch] text-ink [&_h3]:font-ravioli-display [&_h3]:text-3xl [&_h3]:font-bold"
          content={successMessage}
        />
      </div>
    );

  return (
    <form
      aria-busy={submitting}
      className="flex flex-col gap-6"
      noValidate
      onSubmit={submit}
      ref={formRef}
    >
      {submissionFailed ? (
        <div
          className="border border-selection bg-surface p-5 text-foreground outline-none shadow-ravioli-selection-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          ref={failureRef}
          role="alert"
          tabIndex={-1}
        >
          <p className="font-ravioli-display text-2xl font-semibold">
            Your inquiry has not been sent.
          </p>
          <RavioliRichText className="mt-2 text-base text-foreground" content={errorMessage} />
          <button
            className="ravioli-motion-travel mt-4 inline-flex min-h-11 items-center border border-ink bg-canvas px-4 font-ravioli-body text-sm font-semibold text-ink transition-colors hover:border-primary hover:bg-collection hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            onClick={() => formRef.current?.requestSubmit()}
            type="button"
          >
            Try sending again
          </button>
        </div>
      ) : null}
      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const shared = {
          "aria-describedby": error ? errorId : undefined,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          onInput: () => clearFieldError(name),
          required: Boolean(field.required),
        };
        const label = (
          <span className="font-ravioli-body text-[0.8125rem] font-semibold">
            {field.label}
            {field.required ? <span className="ml-1 text-ink-muted">(required)</span> : null}
          </span>
        );
        return (
          <div className="flex flex-col gap-2" key={`${field.label}-${index}`}>
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="size-5 shrink-0 border-line accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                <label htmlFor={inputId}>{label}</label>
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    maxLength={4000}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select {...shared} className={controlClass} defaultValue="">
                    <option disabled value="">
                      {field.placeholder}
                    </option>
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    autoComplete={getAutocomplete(field.fieldType, field.label)}
                    className={controlClass}
                    inputMode={field.fieldType === "phone" ? "tel" : undefined}
                    maxLength={field.fieldType === "email" ? 320 : 200}
                    onInput={(event) => {
                      event.currentTarget.setCustomValidity("");
                      clearFieldError(name);
                    }}
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p
                className="font-ravioli-body text-[0.8125rem] font-semibold text-error-500"
                id={errorId}
              >
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <button
        className="ravioli-motion-travel inline-flex min-h-12 w-full items-center justify-center border border-primary bg-primary px-6 font-ravioli-body text-[0.9375rem] font-semibold text-primary-foreground transition-[background-color,border-color,color] hover:border-primary-700 hover:bg-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44"
        disabled={submitting}
        type="submit"
      >
        {submitting ? `${submitLabel}…` : submitLabel}
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
